import React, {useEffect, useRef, useState, useCallback} from 'react';
import mermaid from 'mermaid';
import {v4 as uuidv4} from 'uuid';
import PropTypes from 'prop-types';
import MermaidDiagramModal from './MermaidDiagramModal';

// Track if mermaid has been initialized globally
let mermaidInitialized = false;

const getMermaidConfig = (isDarkMode) => ({
    startOnLoad: true,
    suppressErrorRendering: false,
    securityLevel: 'loose',
    theme: isDarkMode ? 'dark' : 'forest',
    themeVariables: isDarkMode ? {
        // Keep dark theme's safe contrast defaults; only override accent/brand colors
        primaryBorderColor: '#7c3aed',
        lineColor: '#00b4d8',
        clusterBkg: '#16213e',
        clusterBorder: '#7c3aed',
        titleColor: '#e1e8ed',
        edgeLabelBackground: '#1a1a2e',
        attributeBackgroundColorEven: '#16213e',
        attributeBackgroundColorOdd: '#1a1a2e',
    } : {},
    flowchart: {
        curve: 'basis',
        padding: 20,
        nodeSpacing: 80,
        rankSpacing: 80,
        diagramPadding: 20,
        htmlLabels: true,
        useMaxWidth: true
    },
    sequence: {
        diagramMarginX: 50,
        diagramMarginY: 10,
        actorMargin: 50,
        width: 150,
        height: 65,
        boxMargin: 10,
        boxTextMargin: 5,
        noteMargin: 10,
        messageMargin: 35
    },
    gantt: {
        titleTopMargin: 25,
        barHeight: 20,
        barGap: 4,
        topPadding: 50,
        leftPadding: 75,
        gridLineStartPadding: 35,
        fontSize: 11,
        numberSectionStyles: 4
    }
});

const Mermaid = ({chart}) => {
    const mermaidContainer = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [svgHtml, setSvgHtml] = useState('');
    const [themeVersion, setThemeVersion] = useState(0);

    // Initialize mermaid and react to dark mode class changes
    useEffect(() => {
        if (!mermaidInitialized) {
            const isDarkMode = document.body.classList.contains('dark-mode');
            mermaid.initialize(getMermaidConfig(isDarkMode));
            mermaidInitialized = true;
        }

        // Watch for dark-mode class changes to re-initialize and re-render
        const observer = new MutationObserver(() => {
            const isDarkMode = document.body.classList.contains('dark-mode');
            mermaid.initialize(getMermaidConfig(isDarkMode));
            setThemeVersion(v => v + 1);
        });

        observer.observe(document.body, {attributes: true, attributeFilter: ['class']});

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const renderDiagram = async () => {
            const container = mermaidContainer.current;
            if (!container) return;

            try {
                const {svg} = await mermaid.render(
                    `mermaid-${uuidv4()}`,
                    chart
                );
                container.innerHTML = svg;
            } catch (error) {
                container.innerHTML = `<div class="mermaid-error">Oops! 🤖 The code for this diagram contained an error and couldn't be displayed.<br/>Please try rephrasing your request.<br/><a class="mermaid-error-show" href="#">Show details...</a><div class="mermaid-error-details" style="display:none;"><pre>${error.message}</pre><pre>${chart}</pre><a class="mermaid-error-hide" href="#">Hide details...</a></div></div>`;
                container.classList.add('mermaid-error-container');
                const showLink = container.querySelector('.mermaid-error-show');
                const hideLink = container.querySelector('.mermaid-error-hide');
                const details = container.querySelector('.mermaid-error-details');

                function showDetailsHandler(e) {
                    e.preventDefault();
                    showLink.style.display = 'none';
                    details.style.display = 'block';
                }
                function hideDetailsHandler(e) {
                    e.preventDefault();
                    details.style.display = 'none';
                    showLink.style.display = 'block';
                }

                if (showLink && details) {
                    showLink.addEventListener('click', showDetailsHandler);
                }
                if (hideLink && details) {
                    hideLink.addEventListener('click', hideDetailsHandler);
                }
            }
        };

        renderDiagram();
    }, [chart, themeVersion]); // Re-render when chart or theme changes

    // Helper to normalize SVG for modal
    const normalizeSvgForModal = (svgString) => {
        if (!svgString) return svgString;
        // Use regex to inject width and height
        return svgString.replace(
            /<svg([^>]*)/i,
            '<svg$1 width="100%" height="100%"'
        );
    };

    // Click handler for SVG
    const handleDiagramClick = useCallback((e) => {
        const svg = e.target.closest('svg');
        if (svg) {
            const normalized = normalizeSvgForModal(svg.outerHTML);
            setSvgHtml(normalized);
            setModalOpen(true);
        }
    }, []);

    return (
        <>
            <div className="mermaid-diagram" onClick={handleDiagramClick} ref={mermaidContainer} />
            <MermaidDiagramModal onClose={() => setModalOpen(false)} open={modalOpen} svgHtml={svgHtml} />
        </>
    );
};

Mermaid.propTypes = {
    chart: PropTypes.string.isRequired
};

// Always return memoized component
export default React.memo(Mermaid);
